import React, { Fragment, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core'
import {
  mdiCheckAll,
  mdiCircle,
  mdiEyeCheckOutline,
  mdiEyeOffOutline,
  mdiFilterMultiple,
  mdiInboxArrowDown,
} from '@mdi/js'
import Icon from '@mdi/react'
import { useDispatch } from 'react-redux'

import BaseFilterModal from '../../../components/hooks/BaseFilterModal'
import Pagination from '../../../components/hooks/BasePagination'
import {
  StyledTableCellBody,
  StyledTableCellHeader,
} from '../../../components/hooks/BaseTableComponents'
import filterStylesComponents from '../../../assets/filterComponentsStyles'
import colors from '../../../assets/colors'
import { fetchRecipients } from '../AlertsActions'

import { formatDateTime } from './helpers'

// Status atual da lavanderia (não o do momento do disparo)
const LAUNDRY_STATUS = {
  0: { label: 'Inativa', color: colors.error },
  1: { label: 'Ativa', color: colors.sucess },
  2: { label: 'Em teste', color: colors.secondary },
  3: { label: 'Teste expirado', color: colors.info },
}

const useStyles = makeStyles({
  summary: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  summaryValue: {
    fontSize: '14px',
    color: colors.primary,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  progress: {
    flex: 1,
    minWidth: 160,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.lightGray,
  },
  progressBar: {
    borderRadius: 4,
    backgroundColor: colors.sucess,
  },
  // Cada lavanderia ocupa duas <tr> (linha principal + linha de detalhes),
  // no mesmo modelo do lançamento de contas
  mainCell: {
    borderBottom: 'none',
    paddingTop: '8px',
    paddingBottom: '2px',
  },
  detailCell: {
    paddingTop: '0',
    paddingBottom: '8px',
  },
  detailChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  chip: {
    height: '20px',
    fontSize: '12px',
    color: colors.textPrimary,
    '& .MuiChip-icon': {
      marginLeft: '6px',
      marginRight: '-2px',
    },
  },
  tableContainer: {
    maxHeight: '50vh',
  },
})

const INITIAL_FILTERS = { rows: 10, page: 1, name: '', read: '' }

const RecipientsModal = ({ alert, onClose }) => {
  const classes = useStyles()
  const styles = filterStylesComponents()
  const dispatch = useDispatch()

  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [currentFilters, setCurrentFilters] = useState({ name: '', read: '' })
  const [recipients, setRecipients] = useState([])
  const [meta, setMeta] = useState({})

  useEffect(() => {
    dispatch(fetchRecipients(alert.id, filters)).then(resp => {
      if (!resp?.data) return
      setRecipients(resp.data)
      setMeta(resp.meta || {})
    })
  }, [filters])

  const handleChange = e => {
    const { name, value } = e.target
    setCurrentFilters({ ...currentFilters, [name]: value })
  }

  const handleSubmit = e => {
    e.preventDefault()
    setFilters({ ...filters, ...currentFilters, page: 1 })
  }

  const readCount = alert.read_count || 0
  const total = alert.recipients_count || 0
  const percent = total ? Math.round((readCount / total) * 100) : 0

  return (
    <BaseFilterModal
      open
      hiddenHelpButton
      handleOpen={onClose}
      paperWidth="920px"
      title={`Leituras — ${alert.title}`}
    >
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} className={classes.summary}>
            <Typography className={classes.summaryValue}>
              {`${readCount} de ${total} lavanderias leram (${percent}%)`}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={percent}
              className={classes.progress}
              classes={{ bar: classes.progressBar }}
            />
          </Grid>

          <Grid item xs={12}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={5} md={4}>
                  <TextField
                    name="name"
                    variant="standard"
                    label="Filtre por lavanderia"
                    value={currentFilters.name}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    select
                    name="read"
                    variant="standard"
                    label="Leitura"
                    value={currentFilters.read}
                    onChange={handleChange}
                    fullWidth
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="1">Leram</MenuItem>
                    <MenuItem value="0">Não leram</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm="auto">
                  <Button type="submit" className={styles.btnMoreFilters}>
                    <Icon
                      path={mdiFilterMultiple}
                      size={1}
                      color={colors.secondary}
                    />
                    {` Filtrar`}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>

          <Grid item xs={12}>
            <TableContainer className={classes.tableContainer}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <StyledTableCellHeader align="left">
                      Lavanderia
                    </StyledTableCellHeader>
                    <StyledTableCellHeader align="right" style={{ width: 220 }}>
                      Lido por
                    </StyledTableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipients.map(recipient => {
                    const status =
                      LAUNDRY_STATUS[recipient.laundry_client_status]

                    const details = [
                      status && {
                        key: 'status',
                        icon: mdiCircle,
                        iconColor: status.color,
                        label: status.label,
                        title: 'Status atual da lavanderia',
                      },
                      {
                        key: 'read',
                        icon: recipient.read ? mdiCheckAll : mdiEyeOffOutline,
                        iconColor: recipient.read ? colors.sucess : undefined,
                        label: recipient.read ? 'Lido' : 'Não lido',
                        title: 'Leitura',
                      },
                      {
                        key: 'received',
                        icon: mdiInboxArrowDown,
                        label: `Recebido em ${formatDateTime(
                          recipient.received_at
                        )}`,
                        title: 'Recebido em',
                      },
                      recipient.read_at && {
                        key: 'read_at',
                        icon: mdiEyeCheckOutline,
                        label: `Lido em ${formatDateTime(recipient.read_at)}`,
                        title: 'Lido em',
                      },
                    ].filter(Boolean)

                    return (
                      <Fragment key={recipient.laundry_client_id}>
                        <TableRow>
                          <StyledTableCellBody
                            align="left"
                            className={classes.mainCell}
                          >
                            {recipient.laundry_client_name}
                          </StyledTableCellBody>
                          <StyledTableCellBody
                            align="right"
                            className={classes.mainCell}
                          >
                            {recipient.read_by?.name || '-'}
                          </StyledTableCellBody>
                        </TableRow>

                        <TableRow>
                          <StyledTableCellBody
                            align="left"
                            colSpan={2}
                            className={classes.detailCell}
                          >
                            <Box className={classes.detailChips}>
                              {details.map(item => (
                                <Chip
                                  key={item.key}
                                  size="small"
                                  variant="outlined"
                                  className={classes.chip}
                                  icon={
                                    <Icon
                                      path={item.icon}
                                      size={
                                        item.icon === mdiCircle ? 0.4 : 0.55
                                      }
                                      color={item.iconColor}
                                    />
                                  }
                                  label={item.label}
                                  title={item.title}
                                />
                              ))}
                            </Box>
                          </StyledTableCellBody>
                        </TableRow>
                      </Fragment>
                    )
                  })}

                  {!recipients.length && (
                    <TableRow>
                      <StyledTableCellBody colSpan={2} align="center">
                        Nenhuma lavanderia encontrada
                      </StyledTableCellBody>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Pagination
              from={meta?.from}
              to={meta?.to}
              rowsPerPage={filters.rows}
              rows={meta?.total}
              page={filters.page}
              handleChangeRowsPerPage={e =>
                setFilters({ ...filters, rows: e.target.value, page: 1 })
              }
              handleChangePage={(e, page) => setFilters({ ...filters, page })}
            />
          </Grid>
        </Grid>
      </Grid>
    </BaseFilterModal>
  )
}

export default RecipientsModal
